/**
 * AudioPlayer -- Inline audio player for podcasts and radio streams.
 *
 * Features:
 * - Play/pause with animated button
 * - Seekable progress bar
 * - Current time / duration display
 * - Speed control (0.75x, 1x, 1.25x, 1.5x, 2x)
 * - Volume slider
 * - Mini-mode (persistent bar at bottom when navigating away)
 */

export interface AudioPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  speed: number;
  volume: number;
  title: string;
  source: string;
}

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

export class AudioPlayer {
  private audio: HTMLAudioElement;
  private container: HTMLElement | null = null;
  private miniBar: HTMLElement | null = null;
  private state: AudioPlayerState;
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private onCloseCallback: (() => void) | null = null;

  constructor() {
    this.audio = new Audio();
    this.audio.preload = 'metadata';
    this.state = {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      speed: 1,
      volume: 0.8,
      title: '',
      source: '',
    };

    this.audio.addEventListener('loadedmetadata', () => {
      this.state.duration = this.audio.duration || 0;
      this.updateDisplay();
    });

    this.audio.addEventListener('ended', () => {
      this.state.isPlaying = false;
      this.stopProgressTracking();
      this.updateDisplay();
    });

    this.audio.addEventListener('error', () => {
      this.state.isPlaying = false;
      this.stopProgressTracking();
    });
  }

  load(url: string, title: string, source: string): void {
    this.audio.src = url;
    this.state.title = title;
    this.state.source = source;
    this.state.currentTime = 0;
    this.state.duration = 0;
    this.state.isPlaying = false;
    this.audio.volume = this.state.volume;
    this.audio.playbackRate = this.state.speed;
  }

  render(container: HTMLElement, t: (key: string) => string, onClose?: () => void): void {
    this.container = container;
    this.onCloseCallback = onClose || null;

    container.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.className = 'audio-player';
    wrapper.style.cssText = `
      background: var(--card-bg); border-radius: 20px; padding: 20px;
      box-shadow: var(--shadow-md); max-width: 480px; margin: 0 auto;
    `;

    wrapper.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
        <div style="flex:1;min-width:0;">
          <p class="text-sm font-bold truncate" style="color:var(--text-primary);">${this._escapeHtml(this.state.title)}</p>
          <p class="text-xs" style="color:var(--text-secondary);">${this._escapeHtml(this.state.source)}</p>
        </div>
        <button class="ap-close" aria-label="${t('common_close')}" style="width:32px;height:32px;border-radius:50%;border:none;background:var(--bg-secondary);color:var(--text-secondary);cursor:pointer;display:flex;align-items:center;justify-content:center;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Progress bar -->
      <div class="ap-progress-wrap" style="margin-bottom:8px;cursor:pointer;padding:4px 0;">
        <div style="height:4px;border-radius:2px;background:var(--border);position:relative;">
          <div class="ap-progress-fill" style="height:100%;border-radius:2px;background:var(--accent);width:0%;transition:width 200ms;"></div>
          <div class="ap-progress-thumb" style="position:absolute;top:50%;transform:translate(-50%,-50%);width:14px;height:14px;border-radius:50%;background:var(--accent);box-shadow:0 2px 4px rgba(0,0,0,0.2);left:0%;transition:left 200ms;"></div>
        </div>
      </div>

      <!-- Time display -->
      <div style="display:flex;justify-content:space-between;margin-bottom:16px;">
        <span class="ap-current-time text-xs" style="color:var(--text-secondary);">0:00</span>
        <span class="ap-duration text-xs" style="color:var(--text-secondary);">0:00</span>
      </div>

      <!-- Controls -->
      <div style="display:flex;align-items:center;justify-content:center;gap:20px;margin-bottom:16px;">
        <!-- Rewind 15s -->
        <button class="ap-rewind" aria-label="${t('watch_rewind')}" style="width:40px;height:40px;border-radius:50%;border:none;background:var(--bg-secondary);color:var(--text-primary);cursor:pointer;display:flex;align-items:center;justify-content:center;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/><text x="12" y="16" text-anchor="middle" fill="currentColor" stroke="none" font-size="8" font-weight="bold">15</text></svg>
        </button>

        <!-- Play/Pause -->
        <button class="ap-play" aria-label="${t('watch_play')}" style="width:56px;height:56px;border-radius:50%;border:none;background:var(--accent);color:white;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,0.15);transition:transform 150ms;">
          <svg class="ap-play-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
          <svg class="ap-pause-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="display:none;"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
        </button>

        <!-- Forward 15s -->
        <button class="ap-forward" aria-label="${t('watch_forward')}" style="width:40px;height:40px;border-radius:50%;border:none;background:var(--bg-secondary);color:var(--text-primary);cursor:pointer;display:flex;align-items:center;justify-content:center;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/><text x="12" y="16" text-anchor="middle" fill="currentColor" stroke="none" font-size="8" font-weight="bold">15</text></svg>
        </button>
      </div>

      <!-- Speed & Volume -->
      <div style="display:flex;align-items:center;justify-content:space-between;">
        <button class="ap-speed text-xs font-bold px-3 py-1 rounded-full" style="background:var(--bg-secondary);color:var(--text-primary);border:none;cursor:pointer;">
          ${this.state.speed}x
        </button>
        <div style="display:flex;align-items:center;gap:8px;flex:1;max-width:140px;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
          <input type="range" class="ap-volume" min="0" max="100" value="${Math.round(this.state.volume * 100)}" style="flex:1;accent-color:var(--accent);height:4px;" aria-label="Volume"/>
        </div>
      </div>
    `;

    container.appendChild(wrapper);
    this._bindEvents(wrapper);
    this.updateDisplay();
  }

  renderMiniBar(parent: HTMLElement, t: (key: string) => string): HTMLElement {
    if (this.miniBar) {
      this.miniBar.remove();
    }

    const bar = document.createElement('div');
    bar.className = 'audio-mini-bar';
    bar.style.cssText = `
      position: fixed; bottom: 72px; left: 8px; right: 8px; z-index: 50;
      background: var(--card-bg); border-radius: 16px; padding: 10px 16px;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.12); display: flex; align-items: center; gap: 12px;
      max-width: 480px; margin: 0 auto;
    `;

    bar.innerHTML = `
      <button class="ap-mini-play" aria-label="${t('watch_play')}" style="width:36px;height:36px;border-radius:50%;border:none;background:var(--accent);color:white;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
        <svg class="ap-play-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
        <svg class="ap-pause-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="display:none;"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
      </button>
      <div style="flex:1;min-width:0;">
        <p class="text-xs font-medium truncate" style="color:var(--text-primary);">${this._escapeHtml(this.state.title)}</p>
        <div style="height:2px;border-radius:1px;background:var(--border);margin-top:4px;">
          <div class="ap-mini-progress" style="height:100%;border-radius:1px;background:var(--accent);width:0%;"></div>
        </div>
      </div>
      <button class="ap-mini-close" aria-label="${t('common_close')}" style="width:28px;height:28px;border-radius:50%;border:none;background:transparent;color:var(--text-secondary);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    `;

    const playBtn = bar.querySelector('.ap-mini-play') as HTMLButtonElement;
    playBtn?.addEventListener('click', () => this.togglePlay());

    const closeBtn = bar.querySelector('.ap-mini-close') as HTMLButtonElement;
    closeBtn?.addEventListener('click', () => {
      this.stop();
      bar.remove();
      this.miniBar = null;
    });

    parent.appendChild(bar);
    this.miniBar = bar;
    this._updateMiniBar();
    return bar;
  }

  togglePlay(): void {
    if (this.state.isPlaying) {
      this.audio.pause();
      this.state.isPlaying = false;
      this.stopProgressTracking();
    } else {
      this.audio.play().catch(() => { /* user interaction required */ });
      this.state.isPlaying = true;
      this.startProgressTracking();
    }
    this.updateDisplay();
  }

  stop(): void {
    this.audio.pause();
    this.audio.currentTime = 0;
    this.state.isPlaying = false;
    this.state.currentTime = 0;
    this.stopProgressTracking();
    this.updateDisplay();
  }

  isActive(): boolean {
    return this.state.isPlaying || this.audio.currentTime > 0;
  }

  getState(): AudioPlayerState {
    return { ...this.state };
  }

  destroy(): void {
    this.stop();
    this.audio.src = '';
    if (this.miniBar) {
      this.miniBar.remove();
      this.miniBar = null;
    }
    this.container = null;
  }

  private startProgressTracking(): void {
    this.stopProgressTracking();
    this.progressInterval = setInterval(() => {
      this.state.currentTime = this.audio.currentTime;
      this.updateDisplay();
      this._updateMiniBar();
    }, 250);
  }

  private stopProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private updateDisplay(): void {
    if (!this.container) return;

    const fill = this.container.querySelector('.ap-progress-fill') as HTMLElement;
    const thumb = this.container.querySelector('.ap-progress-thumb') as HTMLElement;
    const currentEl = this.container.querySelector('.ap-current-time');
    const durationEl = this.container.querySelector('.ap-duration');
    const playIcon = this.container.querySelector('.ap-play-icon') as HTMLElement;
    const pauseIcon = this.container.querySelector('.ap-pause-icon') as HTMLElement;

    const pct = this.state.duration > 0 ? (this.state.currentTime / this.state.duration) * 100 : 0;

    if (fill) fill.style.width = `${pct}%`;
    if (thumb) thumb.style.left = `${pct}%`;
    if (currentEl) currentEl.textContent = this._formatTime(this.state.currentTime);
    if (durationEl) durationEl.textContent = this._formatTime(this.state.duration);

    if (playIcon && pauseIcon) {
      playIcon.style.display = this.state.isPlaying ? 'none' : '';
      pauseIcon.style.display = this.state.isPlaying ? '' : 'none';
    }
  }

  private _updateMiniBar(): void {
    if (!this.miniBar) return;

    const progress = this.miniBar.querySelector('.ap-mini-progress') as HTMLElement;
    const playIcon = this.miniBar.querySelector('.ap-play-icon') as HTMLElement;
    const pauseIcon = this.miniBar.querySelector('.ap-pause-icon') as HTMLElement;

    const pct = this.state.duration > 0 ? (this.state.currentTime / this.state.duration) * 100 : 0;
    if (progress) progress.style.width = `${pct}%`;

    if (playIcon && pauseIcon) {
      playIcon.style.display = this.state.isPlaying ? 'none' : '';
      pauseIcon.style.display = this.state.isPlaying ? '' : 'none';
    }
  }

  private _bindEvents(wrapper: HTMLElement): void {
    // Play/Pause
    wrapper.querySelector('.ap-play')?.addEventListener('click', () => this.togglePlay());

    // Close
    wrapper.querySelector('.ap-close')?.addEventListener('click', () => {
      this.stop();
      if (this.onCloseCallback) this.onCloseCallback();
    });

    // Rewind 15s
    wrapper.querySelector('.ap-rewind')?.addEventListener('click', () => {
      this.audio.currentTime = Math.max(0, this.audio.currentTime - 15);
      this.state.currentTime = this.audio.currentTime;
      this.updateDisplay();
    });

    // Forward 15s
    wrapper.querySelector('.ap-forward')?.addEventListener('click', () => {
      this.audio.currentTime = Math.min(this.audio.duration || 0, this.audio.currentTime + 15);
      this.state.currentTime = this.audio.currentTime;
      this.updateDisplay();
    });

    // Speed
    wrapper.querySelector('.ap-speed')?.addEventListener('click', (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const idx = SPEEDS.indexOf(this.state.speed);
      this.state.speed = SPEEDS[(idx + 1) % SPEEDS.length];
      this.audio.playbackRate = this.state.speed;
      btn.textContent = `${this.state.speed}x`;
    });

    // Volume
    const volumeInput = wrapper.querySelector('.ap-volume') as HTMLInputElement;
    volumeInput?.addEventListener('input', () => {
      this.state.volume = parseInt(volumeInput.value, 10) / 100;
      this.audio.volume = this.state.volume;
    });

    // Progress bar seek
    const progressWrap = wrapper.querySelector('.ap-progress-wrap') as HTMLElement;
    progressWrap?.addEventListener('click', (e) => {
      const rect = progressWrap.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      if (this.state.duration > 0) {
        this.audio.currentTime = pct * this.state.duration;
        this.state.currentTime = this.audio.currentTime;
        this.updateDisplay();
      }
    });
  }

  private _formatTime(seconds: number): string {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  private _escapeHtml(text: string): string {
    const d = document.createElement('div');
    d.textContent = text;
    return d.innerHTML;
  }
}
